import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { RootState } from '..'
import questionsService from '../../services/questionsService'
import usersService from '../../services/usersService'
import { MainObject } from '../reports/types'
import { ISubjectDTO, IVictimDTO2 } from '../users/types'
import { findAnswer } from './questionsSelector'
import { IAnswer, questionsState, INewVictim } from './types'

export const findItemByUuid = (items: IAnswer[], uuid: string): IAnswer | null => {
  for (const item of items) {
    if (item.uuid === uuid) {
      return item
    }
    const subitem = findItemByUuid(item.subanswers, uuid)
    if (subitem) {
      return subitem
    }
  }
  return null
}

const addVictimIdToAnswers = (answers: MainObject[], victimId: number) => {
  return answers.map(answer => {
    return {
      ...answer,
      answer: {
        ...answer?.answer,
        victim: victimId,
      },
    }
  })
}

const initialState: questionsState = {
  newCaseAnswers: [],
  reporting_party: {
    full_name: '',
    phone_number: '',
    email: '',
    work_site: '',
    city: '',
    state: '',
    work_country: '',
    description: 'unknown',
    person_type: 'UNKNOWN',
  },
  custom_fields: [],
  victims: [],
  subjects: [],
  subjectsCustomFields: [],
  newCaseUrgent: true,
  victimAnswers: [],
}

export const postNewCase = createAsyncThunk(
  'questions/postNewCase',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState
      const { image, ...reporting_party } = state.questions.reporting_party
      const victims = state?.questions?.victims
      const subjects = state?.questions?.subjects
      const dataToSend = {
        report: {
          name: `Report ${Math.random()}`,
          organization: 1,
          report_status: 1,
          is_urgent: false,
          workplace: {
            work_site: 'Work Site 1',
            work_city: 'Work City 1',
            work_state: 'Work State 1',
            work_country: 'Work Country 1',
          },
        },
        answers: state?.questions?.newCaseAnswers,
        reporters: [
          {
            reporting_party: { ...reporting_party, is_urgent: false },
          },
        ],
        victims: [],
        subjects: [],
      }
      const { data: newCaseResponseData } = await questionsService.postNewCase(dataToSend)
      const { data: reportData } = await questionsService.getReportByUniqueCode(
        newCaseResponseData?.unique_code,
      )
      const reportId = reportData[0]?.id

      if (image) {
        const reporterData = reportData[0]?.reporters[0]
        const reporterImage = new File([image], 'name.jpeg', {
          type: 'image/jpeg',
          lastModified: new Date().getTime(),
        })

        await usersService.patchReportingParty({
          reporting_party: { ...reporterData, image: reporterImage },
        })
      }
      const victimPromises = victims.map(async victim => {
        const { image, victimId, ...reducedVictim } = victim
        const victimImage = image
          ? new File([image], 'name.jpeg', {
              type: 'image/jpeg',
              lastModified: new Date().getTime(),
            })
          : undefined
        const newVictim = {
          victim: {
            ...reducedVictim,
            report: reportId,
            ...(victimImage ? { image: victimImage } : {}),
          },
        } as unknown as IVictimDTO2

        return await usersService.postNewVictimNewCase(newVictim).then(res => {
          return { victim: res.data, victimId }
        })
      })

      Promise.all(victimPromises)
        .then(res => {
          // eslint-disable-next-line

          const victimAnswersPromises = state.questions.victimAnswers.map(async answers => {
            const newVictim = res.find(data => {
              return data?.victimId == answers?.victimId
            })
            const updatedAnswers = addVictimIdToAnswers(
              answers?.answers,
              newVictim?.victim?.victim?.id,
            )
            return await questionsService.postVictimAnswers(reportId, updatedAnswers, 'REPORTER')
          })

          Promise.all(victimAnswersPromises)
        })
        .catch(error => {
          // eslint-disable-next-line
          // console.log('victims error: ', error)
        })

      const subjectPromises = subjects?.map(async subject => {
        const { image, subjectId, subjectRenderType, ...reducedsubject } = subject
        const subjectImage = image
          ? new File([image], 'name.jpeg', {
              type: 'image/jpeg',
              lastModified: new Date().getTime(),
            })
          : undefined
        const newsubject = {
          subject: {
            ...reducedsubject,
            report: reportId,
            ...(subjectImage ? { image: subjectImage } : {}),
          },
          custom_fields:
            state?.questions?.subjectsCustomFields?.find(
              subjectData => subjectData?.subjectId === subjectId,
            )?.custom_fields ?? [],
        } as unknown as ISubjectDTO

        return await usersService.postNewSubject(newsubject)
      })

      return newCaseResponseData
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue('Failed creating New Case')
      }
    }
  },
)

const questionsSlice = createSlice({
  name: 'questions',
  initialState,
  reducers: {
    resetAllFields: (state) => {
      state.reporting_party = initialState.reporting_party
      state.custom_fields = initialState.custom_fields
      state.victims = initialState.victims
      state.subjects = initialState.subjects
      state.subjectsCustomFields = initialState.subjectsCustomFields
      state.victimAnswers = initialState.victimAnswers
    },
    setNewCaseAnswers: (state, { payload }) => {
      state.newCaseAnswers = payload.answers
    },
    updateNewCaseAnswerOption: (state, { payload }) => {
      const item = findItemByUuid(state.newCaseAnswers, payload?.answerUUID)
      if (item) {
        item.options = payload?.answerText
      }
    },
    updateNewCaseAnswerText: (state, { payload }) => {
      const item = findItemByUuid(state.newCaseAnswers, payload?.answerUUID)
      if (item) {
        item.answer.text = payload?.answerText
      }
    },
    updateVictimAnswerOption: (state, { payload }) => {
      const victim = state?.victimAnswers?.find(victim => victim.victimId === payload?.victimId)

      if (victim) {
        const item = findAnswer(victim?.answers, payload?.answerUUID)
        if (item) {
          item.options = payload?.answerText
        }
      }
    },
    updateVictimAnswerText: (state, { payload }) => {
      const victim = state?.victimAnswers?.find(victim => victim.victimId === payload?.victimId)

      if (victim) {
        const item = findAnswer(victim?.answers, payload?.answerUUID)
        if (item) {
          item.answer.text = payload?.answerText
        }
      }
    },
    setReporterValues: (state, action) => {
      const { field, value } = action.payload
      state.reporting_party[field] = value as File
    },
    addCustomFields: (state, { payload }) => {
      state.custom_fields = payload?.custom_fields
    },
    updateCustomField: (state, { payload }) => {
      const { title, value } = payload
      state.custom_fields = state.custom_fields.map(field => {
        if (field?.title === title) {
          return {
            ...field,
            description: value,
          }
        }
        return field
      })
    },
    addCustomSubjectFields: (state, { payload }) => {
      const { subjectId, fields } = payload
      state.subjectsCustomFields.push({ subjectId, custom_fields: fields })
    },
    updateSubjectsCustomField: (state, { payload }) => {
      const { subjectId, title, value } = payload
      const subjectData = state.subjectsCustomFields.find(
        subject => subject?.subjectId === subjectId,
      )
      if (subjectData) {
        subjectData.custom_fields = subjectData?.custom_fields.map(field => {
          if (field?.title === title) {
            return {
              ...field,
              description: value,
            }
          }
          return field
        })
      }
    },
    addNewVictimForm: (state, { payload }) => {
      state.victims.push({
        victimId: payload?.victimId,
        full_name: '',
        phone: '',
        email: '',
        work_site: '',
        city: '',
        state: '',
        work_country: '',
        person_type: '',
        image: '',
        answers: payload?.answers,
      })
      state.victimAnswers.push({ victimId: payload?.victimId, answers: payload?.defaultAnswers })
    },
    removeVictimForm: (state, { payload }) => {
      const filteredVictims = state?.victims?.filter(
        victim => victim?.victimId !== payload?.victimId,
      )
      state.victims = filteredVictims
    },
    setVictimValues: (state, action) => {
      const { field, value, victimId } = action.payload
      state.victims = state?.victims.map((victim: INewVictim) => {
        if (victim?.victimId === victimId) {
          return { ...victim, [field]: value }
        }
        return victim
      })
    },
    setVictimsAnswers: (state, { payload }) => {
      state.victims = state?.victims.map((victim: INewVictim) => {
        if (victim?.victimId === payload?.victimId) {
          return { ...victim, answers: payload?.answers }
        }
        return victim
      })
    },
    addNewSubjectForm: (state, { payload }) => {
      state.subjects.push({
        subjectId: payload?.subjectId,
        full_name: '',
        description: '',
        person_type: 'UNKNOWN',
        image: '',
        subjectRenderType: payload?.subjectRenderType ?? '',
      })
    },
    removeSubjectForm: (state, { payload }) => {
      const filteredSubjects = state?.subjects?.filter(
        subject => subject?.subjectId !== payload?.subjectId,
      )
      state.subjects = filteredSubjects
    },
    setSubjectValues: (state, action) => {
      const { field, value, subjectId } = action.payload
      state.subjects = state?.subjects.map((subject: { subjectId: any }) => {
        if (subject?.subjectId === subjectId) {
          return { ...subject, [field]: value }
        }
        return subject
      })
    },
    resetSubjectValues: (state, action) => {
      const { subjectId, subjectRenderType } = action.payload
      state.subjects = state?.subjects.map((subject: { subjectId: any }) => {
        if (subject?.subjectId === subjectId) {
          return {
            subjectId: subjectId,
            full_name: '',
            description: '',
            person_type: 'UNKNOWN',
            image: '',
            subjectRenderType: subjectRenderType ?? '',
          }
        }
        return subject
      })
    },
  },
})

export const {
  resetAllFields,
  setNewCaseAnswers,
  updateNewCaseAnswerOption,
  updateNewCaseAnswerText,
  setReporterValues,
  addCustomFields,
  updateCustomField,
  addNewVictimForm,
  removeVictimForm,
  setVictimValues,
  setVictimsAnswers,
  updateVictimAnswerOption,
  updateVictimAnswerText,
  addNewSubjectForm,
  removeSubjectForm,
  setSubjectValues,
  resetSubjectValues,
  updateSubjectsCustomField,
  addCustomSubjectFields,
} = questionsSlice.actions

export default questionsSlice.reducer
